import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Building2, Globe, Shield, Palette, Save, RefreshCw, Check, X,
  Users, MapPin, Crown, Zap, Package, BarChart3, GraduationCap, ClipboardList
} from "lucide-react";

const PLANS = [
  { id: "starter", name: "Starter", price: 49, maxLoc: 1, maxStaff: 5, maxClients: 500, features: ["multi_location", "reports"] },
  { id: "professional", name: "Professional", price: 149, maxLoc: 5, maxStaff: 25, maxClients: 5000, features: ["multi_location", "loyalty", "waitlist", "ai_tools", "training", "reports", "automations"] },
  { id: "enterprise", name: "Enterprise", price: 399, maxLoc: 50, maxStaff: 200, maxClients: 50000, features: ["multi_location", "loyalty", "waitlist", "ai_tools", "training", "reports", "automations", "white_label", "api_access", "priority_support"] },
];

const FEATURE_LABELS = {
  multi_location: { label: "Multi-Location", icon: MapPin },
  loyalty: { label: "Loyalty Program", icon: Crown },
  waitlist: { label: "Waitlist", icon: ClipboardList },
  ai_tools: { label: "AI Tools", icon: Zap },
  training: { label: "Training Studio", icon: GraduationCap },
  reports: { label: "Reports & Analytics", icon: BarChart3 },
  automations: { label: "Automations", icon: Package },
  white_label: { label: "White Label", icon: Palette },
  api_access: { label: "API Access", icon: Globe },
  priority_support: { label: "Priority Support", icon: Shield },
};

export default function WhiteLabelPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await api.get("/tenant/config");
      setConfig(res.data);
      setForm({
        org_name: res.data?.organization?.name || "",
        plan: res.data?.subscription?.plan || "professional",
        custom_domain: res.data?.subscription?.custom_domain || "",
        white_label: res.data?.subscription?.white_label || false,
        billing_email: res.data?.subscription?.billing_email || "",
        max_locations: res.data?.subscription?.max_locations || 5,
        max_staff: res.data?.subscription?.max_staff || 25,
        max_clients: res.data?.subscription?.max_clients || 5000,
      });
    } catch { toast.error("Failed to load config"); }
    setLoading(false);
  };

  useEffect(() => { fetchConfig(); }, []);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put("/tenant/config", form);
      toast.success("Configuration saved");
      fetchConfig();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const selectPlan = (planId) => {
    const plan = PLANS.find(p => p.id === planId);
    if (plan) {
      setForm(f => ({
        ...f,
        plan: planId,
        max_locations: plan.maxLoc,
        max_staff: plan.maxStaff,
        max_clients: plan.maxClients,
      }));
    }
  };

  if (loading) return <div className="p-8 flex items-center justify-center min-h-[400px]"><RefreshCw className="w-5 h-5 animate-spin text-rose-400" /></div>;

  const sub = config?.subscription || {};
  const currentPlan = PLANS.find(p => p.id === form.plan) || PLANS[1];
  const locs = config?.locations || [];

  return (
    <div data-testid="whitelabel-page" className="p-6 lg:p-8 max-w-[1200px] mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight">White-Label & SaaS Config</h1>
          <p className="text-sm text-slate-400 mt-1">Multi-tenant architecture, branding, and subscription management</p>
        </div>
        <Button size="sm" onClick={saveConfig} disabled={saving} className="bg-slate-900 text-white" data-testid="save-config-btn">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      {/* Organization */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h2 className="font-['Outfit'] font-semibold text-slate-900 flex items-center gap-2"><Building2 className="w-5 h-5 text-slate-600" /> Organization</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Organization Name</label>
            <Input value={form.org_name} onChange={e => setForm(f => ({ ...f, org_name: e.target.value }))} className="border-slate-200" data-testid="org-name-input" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Billing Email</label>
            <Input value={form.billing_email} onChange={e => setForm(f => ({ ...f, billing_email: e.target.value }))} className="border-slate-200" data-testid="billing-email-input" />
          </div>
        </div>
        <div className="flex items-center gap-3 pt-2">
          <p className="text-sm text-slate-600">{locs.length} location{locs.length !== 1 ? "s" : ""} configured</p>
          {locs.map(l => (
            <span key={l.location_id} className="text-xs bg-[#FAFAFA] px-2 py-1 rounded border border-slate-200 text-slate-600 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {l.name}
            </span>
          ))}
        </div>
      </div>

      {/* Subscription Plan */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h2 className="font-['Outfit'] font-semibold text-slate-900 flex items-center gap-2"><Crown className="w-5 h-5 text-[#F59E0B]" /> Subscription Plan</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {PLANS.map(plan => (
            <button
              key={plan.id}
              data-testid={`plan-${plan.id}`}
              onClick={() => selectPlan(plan.id)}
              className={`p-5 rounded-2xl border-2 text-left transition-all ${form.plan === plan.id ? "border-rose-500 bg-[#FAFAF9]" : "border-slate-200 hover:border-rose-300"}`}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-['Outfit'] font-semibold text-slate-900">{plan.name}</h3>
                {form.plan === plan.id && <Check className="w-5 h-5 text-emerald-600" />}
              </div>
              <p className="font-['Outfit'] text-2xl font-semibold text-slate-900">${plan.price}<span className="text-sm font-normal text-slate-400">/mo</span></p>
              <div className="mt-3 space-y-1 text-xs text-slate-600">
                <p>{plan.maxLoc} location{plan.maxLoc !== 1 ? "s" : ""}</p>
                <p>Up to {plan.maxStaff} staff</p>
                <p>Up to {plan.maxClients.toLocaleString()} clients</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Features */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h2 className="font-['Outfit'] font-semibold text-slate-900 flex items-center gap-2"><Zap className="w-5 h-5 text-[#8B5CF6]" /> Plan Features</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {Object.entries(FEATURE_LABELS).map(([key, feat]) => {
            const enabled = currentPlan.features.includes(key);
            const FIcon = feat.icon;
            return (
              <div key={key} data-testid={`feature-${key}`} className={`p-3 rounded-lg border text-center ${enabled ? "border-[#C3E6CB] bg-[#D4EDDA]/30" : "border-slate-200 bg-[#FAFAF9] opacity-50"}`}>
                <FIcon className={`w-5 h-5 mx-auto mb-1.5 ${enabled ? "text-[#155724]" : "text-slate-400"}`} />
                <p className={`text-xs font-medium ${enabled ? "text-[#155724]" : "text-slate-400"}`}>{feat.label}</p>
                {enabled && <Check className="w-3 h-3 mx-auto mt-1 text-emerald-600" />}
              </div>
            );
          })}
        </div>
      </div>

      {/* White Label Settings */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h2 className="font-['Outfit'] font-semibold text-slate-900 flex items-center gap-2"><Palette className="w-5 h-5 text-[#EC4899]" /> White-Label</h2>
        <div className="flex items-center gap-3">
          <Switch
            checked={form.white_label}
            onCheckedChange={v => setForm(f => ({ ...f, white_label: v }))}
            data-testid="white-label-switch"
          />
          <div>
            <p className="text-sm font-medium text-slate-900">Enable White Label Mode</p>
            <p className="text-xs text-slate-400">Remove SalonFlow branding, use your own logo and colors</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Custom Domain</label>
            <Input
              value={form.custom_domain}
              onChange={e => setForm(f => ({ ...f, custom_domain: e.target.value }))}
              placeholder="app.yoursalon.com"
              className="border-slate-200"
              data-testid="custom-domain-input"
            />
          </div>
          <div className="flex items-end">
            <Button variant="outline" size="sm" className="border-slate-200 text-slate-600" onClick={() => window.location.href = "/settings"}>
              <Palette className="w-4 h-4 mr-1.5" /> Edit Branding Colors
            </Button>
          </div>
        </div>
        {form.white_label && (
          <div className="bg-[#D4EDDA] border border-[#C3E6CB] rounded-lg p-3 text-xs text-[#155724]">
            White label mode is enabled. Your custom branding (logo, colors, app name) from Settings will be displayed to all users. "Powered by SalonFlow" badge will be hidden.
          </div>
        )}
      </div>

      {/* Limits */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
        <h2 className="font-['Outfit'] font-semibold text-slate-900 flex items-center gap-2"><Shield className="w-5 h-5 text-[#3B82F6]" /> Tenant Limits</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Max Locations</label>
            <Input type="number" value={form.max_locations} onChange={e => setForm(f => ({ ...f, max_locations: +e.target.value }))} className="border-slate-200" data-testid="max-locations-input" />
            <p className="text-[10px] text-slate-400 mt-0.5">Using {locs.length} of {form.max_locations}</p>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Max Staff</label>
            <Input type="number" value={form.max_staff} onChange={e => setForm(f => ({ ...f, max_staff: +e.target.value }))} className="border-slate-200" data-testid="max-staff-input" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Max Clients</label>
            <Input type="number" value={form.max_clients} onChange={e => setForm(f => ({ ...f, max_clients: +e.target.value }))} className="border-slate-200" data-testid="max-clients-input" />
          </div>
        </div>
      </div>
    </div>
  );
}
